import axios from 'axios'

import { handleErrorAndGenerateMessage } from '../../../functions/errorHandling'

import { formatDateForServer, parseDateFromServer } from '../../../functions/dateHandling'
import { userRegionIsEU } from '../../../functions/userPermissions'
import { getServerUrl } from '../../../functions/getServerUrl'

import { setUser } from '../../login/reducers/loginReducer'

let defaultState = {
  showChartGeneralValidation: false,
  fetchingChart: false,
  errorFetchingChart: false,
  errorFetchingChartMessage: null,
  chartPhase: 1,
  savingChartPhase: false,
  checkingIfChartIsComplete: false,
  patientChartErrorMessage: '',
  chartDateErrorMessage: '',
  deletingChart: false,
  chartCompleted: false,
  dateDiscrepancyApproved: false,
  chartIsIncompleteModalShow: false,
  chartIsIncompleteModalData: [],
  chartIncompleteDuringApprovalProcess: false,
  triggerDateOverrideModal: false,
  hospitalAdmissionDateOverride: null,
  antibioticsUseDateOverride: null,
  infectionCourseDateOverride: null,
  pathogenDateOverride: null,

  // general info
  chartCreatedBy: '',
  trialSiteNumber: '',
  chartReviewerId: '',
  chartReviewerRole: '',
  chartReviewerRoleOther: '',
  patientIdentifier: '',
  site: {},
  screeningFailure: false,
  patientChartUrl: null,
  metInclusionCriteria: null,
  metExclusionCriteria: null,
  informedConsentDate: null,
  localRequirementsExemption: false,
  chartCreatedDate: null,
}

export default function chartFormReducer(state = defaultState, action) {
  let chartPhase, patientChartUrl, chartCompleted, chartCreatedBy, chartCreatedDate
  switch (action.type) {
    case "FETCHING_CHART":
      return { ...state, fetchingChart: true }
    case "HANDLE_ERROR_FETCHING_CHART":
      return {
        ...state, 
        fetchingChart: false, 
        errorFetchingChart: true, 
        errorFetchingChartMessage: action.payload,
      }
    case "DELETING_CHART":
      return { ...state, deletingChart: true }
    case "FINISHED_DELETING_CHART":
      return { ...state, deletingChart: false }
    case "CHART_HAS_BEEN_DELETED":
      return defaultState
    case "CHANGE_EDIT_CHART_PHASE":
      return { ...state, chartPhase: action.payload }
    case "HANDLE_PATIENT_CHART_FIELD_CHANGE":
      return { ...state, [action.payload.key]: action.payload.value }
    case "CHART_FIELD_SAVED":
      return { ...state, savingChartPhase: false }
    case "SAVING_CHART_PHASE":
      return { ...state, savingChartPhase: true, patientChartErrorMessage: '' }
    case "TOGGLE_CHECKING_IF_CHART_IS_COMPLETE":
      return { ...state, checkingIfChartIsComplete: action.payload }
    case "ERROR_SAVING_CHART_PHASE":
      return { ...state, savingChartPhase: false, patientChartErrorMessage: action.payload }
    case "ERROR_SAVING_CHART_DATE":
      return { ...state, savingChartPhase: false, chartDateErrorMessage: action.payload }
    case "RESETTING_CHART_DATE":
      return { ...state, savingChartPhase: false, chartDateErrorMessage: action.payload }
    case "CHART_GENERAL_INFO_HAS_BEEN_SAVED":
      patientChartUrl = action.payload.chart.url
      chartCreatedDate = action.payload.chart.created_date
      chartCompleted = action.payload.chart.completed
      chartCreatedBy = action.payload.chart.created_by
      chartPhase = getChartPhase(state.chartPhase, action.payload)
      return {
        ...state,
        savingChartPhase: false,
        patientChartUrl,
        chartPhase,
        chartCompleted,
        chartCreatedBy,
        chartCreatedDate,
       }
    case "CHART_DEMOGRAPHICS_INFO_HAS_BEEN_SAVED":
      chartPhase = getChartPhase(state.chartPhase, action.payload)
      return { ...state, savingChartPhase: false, chartPhase }
    case "CHART_HOSPITAL_ADMISSION_INFO_HAS_BEEN_SAVED":
      chartPhase = getChartPhase(state.chartPhase, action.payload)
      return { ...state, savingChartPhase: false, chartPhase }
    case "CHART_INFECTION_COURSE_INFO_HAS_BEEN_SAVED":
      chartPhase = getChartPhase(state.chartPhase, action.payload)
      return { ...state, savingChartPhase: false, chartPhase }
    case "SET_PRIMARY_INFECTION_SITE_WARNING_MESSAGE":
      return { ...state, savingChartPhase: false }
    case "SET_CUSTOM_PATHOGEN_WARNING_MESSAGE":
      return { ...state, savingChartPhase: false }
    case "SET_CUSTOM_SUSCEPTIBILITY_TEST_WARNING_MESSAGE":
      return { ...state, savingChartPhase: false }
    case "CHART_ANTIBIOTICS_USE_INFO_HAS_BEEN_SAVED":
      chartPhase = getChartPhase(state.chartPhase, action.payload)
      return { ...state, savingChartPhase: false, chartPhase }
    case "CHART_INCOMPLETE_ON_SUBMIT":
      return {
        ...state,
        chartIsIncompleteModalShow: true,
        chartIsIncompleteModalData: action.payload.chartIsIncompleteModalData,
        chartIncompleteDuringApprovalProcess: action.payload.chartIncompleteDuringApprovalProcess,

      }
    case "CHART_HOSPITALIZATION_OUTCOME_INFO_HAS_BEEN_SAVED":
      if (action.payload.goToNextPhase === true) {
        return defaultState
      } else {
        return { ...state, savingChartPhase: false }
      }
    case "MARK_CHART_AS_COMPLETED":
      return defaultState
    case "TOGGLE_SHOW_CHART_GENERAL_VALIDATION":
      return { ...state, showChartGeneralValidation: action.payload }
    case "SET_CHART_FROM_SERVER_TO_FORM":
      return {
        ...state,
        fetchingChart: false,
        chartPhase: action.payload.chartPhase,
        savingChartPhase: false,
        patientChartErrorMessage: '',
        trialSiteNumber: action.payload.trial_site || '',
        chartReviewerId: action.payload.chart_reviewer || '',
        chartReviewerRole: action.payload.chart_reviewer_role || '',
        chartReviewerRoleOther: action.payload.chart_reviewer_role_other_text || '',
        patientIdentifier: action.payload.patient_identifier || '',
        patientChartUrl: action.payload.url,
        chartCreatedBy: action.payload.created_by,
        chartCompleted: action.payload.completed,
        screeningFailure: action.payload.screening_failure,
        dateDiscrepancyApproved: action.payload.date_discrepancy_approved,
        chartForRevision: action.payload.requires_revisions,
        chartApproved: action.payload.approved,
        site: action.payload.site,
        metInclusionCriteria: action.payload.met_inclusion_criteria,
        metExclusionCriteria: action.payload.met_exclusion_criteria,
        informedConsentDate: parseDateFromServer(action.payload.consent_date),
        localRequirementsExemption: action.payload.local_requirements_exemption || false,
        chartCreatedDate: action.payload.created_date || null,
      }
    default:
      return state
  }
}

const toggleShowChartGeneralValidation = (bool) => {
  return { type: "TOGGLE_SHOW_CHART_GENERAL_VALIDATION", payload: bool }
}

const getChartPhase = (chartPhase, payload) => {
  if (payload.goToNextPhase && payload.goToNextPhase === true) {
    return chartPhase + 1
  }

  return chartPhase
}

export const handleChartPhaseChange = (phase) => {
  return { type: "CHANGE_EDIT_CHART_PHASE", payload: phase}
}

export const handleChartFieldChange = (key, value) => {
  return { type: "HANDLE_PATIENT_CHART_FIELD_CHANGE", payload: { key, value } }
}

export const chartFieldSaved = (key, value) => {
  return { type: "CHART_FIELD_SAVED", payload: { key, value } }
}

export const savingChartPhase = () => {
  return { type: "SAVING_CHART_PHASE" }
}

export const toggleCheckingIfChartIsComplete = (payload) => {
  return { type: "TOGGLE_CHECKING_IF_CHART_IS_COMPLETE", payload }
}

export const errorSavingChartPhase = (error, module, dispatch) => {
  const message = handleErrorAndGenerateMessage(error, module, dispatch)
  return { type: "ERROR_SAVING_CHART_PHASE", payload: message }
}

export const errorSavingChartDate = (error, module) => {
  let message = error.response.data.non_field_errors.join(' ')
  return { type: "ERROR_SAVING_CHART_DATE", payload: message }
}

export const resettingChartDateError = () => {
  return { type: "RESETTING_CHART_DATE", payload: '' }
}

const chartGeneralInfoHasBeenSaved = (chart, goToNextPhase) => {
  return { type: "CHART_GENERAL_INFO_HAS_BEEN_SAVED", payload: { chart, goToNextPhase } }
}

const chartIncompleteOnSubmit = (chartIsIncompleteModalData, chartIncompleteDuringApprovalProcess = false) => {
  return {
    type: "CHART_INCOMPLETE_ON_SUBMIT",
    payload: {
      chartIsIncompleteModalData,
      chartIncompleteDuringApprovalProcess,
    }
  }
}



export const setChartToForm = (chart, startAtPhaseOne) => {

  const payload = chart

  let chartPhase = 1

  if (chart.hospitalization_outcome) {
    chartPhase = 6
  } else if (chart.antibiotics_use) {
    chartPhase = 5
  } else if (chart.infection_course) {
    chartPhase = 4
  } else if (chart.hospital_admission) {
    chartPhase = 3
  } else if (chart.patient_demographics) {
    chartPhase = 2
  }

  if (startAtPhaseOne) {
    chartPhase = 1
  }

  payload.chartPhase = chartPhase
  return { type: "SET_CHART_FROM_SERVER_TO_FORM", payload }
}

export const alertChartHasBeenSaved = (props) => {
  if (props.alert) {
    window.scroll(0, 0)
    props.alert.success('Chart has been saved')
  }
}

export const consentFormIsInvalid = (props) => {
  const {
    currentUser,
    metInclusionCriteria,
    metExclusionCriteria,
    informedConsentDate,
    localRequirementsExemption,
  } = props
  
  let consentDatesAreValid = false
  if (!userRegionIsEU(currentUser)) {
    consentDatesAreValid = true
  } else if (localRequirementsExemption === true) {
    consentDatesAreValid = true
  } else if (userRegionIsEU(currentUser) && informedConsentDate) {
    if (new Date(informedConsentDate).valueOf() < new Date().valueOf()) {
      consentDatesAreValid = true
    }
  }

  return !consentDatesAreValid || metInclusionCriteria !== true || metExclusionCriteria !== false
}

export const consentFormIsNull = (props) => {
  const {
    currentUser,
    metInclusionCriteria,
    metExclusionCriteria,
    informedConsentDate,
    localRequirementsExemption,
  } = props

  let consentDatesAreNull = false

  if (userRegionIsEU(currentUser) && localRequirementsExemption !== true && informedConsentDate === null) {
    consentDatesAreNull = true
  }

  return consentDatesAreNull || metInclusionCriteria === null || metExclusionCriteria === null || localRequirementsExemption === null
}

export const submitChartGeneralInfo = (props, goToNextPhase) => {
  const {
    trialSiteNumber,
    chartReviewerId,
    chartReviewerRole,
    patientIdentifier,
    patientChartUrl,
    currentUser,
    metInclusionCriteria,
    metExclusionCriteria,
    informedConsentDate,
    localRequirementsExemption,
  } = props

  //if chartReviewerRole is not other, clear out field and set to null
  let chartReviewerRoleOther = props.chartReviewerRoleOther
  if (chartReviewerRole !== 'Other') {
    chartReviewerRoleOther = null
  }

  let site = props.site 
  if (!props.site && !props.site.url) {
    site = null
  } else {
    site = props.site.url
  }

  return (dispatch, getState) => {
    const chartIsSaving = getState().chartFormReducer.savingChartPhase
    if (chartIsSaving) {
      return dispatch({ type: '' })
    }

    if ((consentFormIsNull(props) || !trialSiteNumber || !chartReviewerId || !chartReviewerRole || !patientIdentifier || !site) && props.alert && !patientChartUrl) {
      props.alert.show('Please fill in all General Information fields before saving')
      return dispatch(toggleShowChartGeneralValidation(true))
    }

    if (consentFormIsInvalid(props) && !patientChartUrl) {
      props.alert.show('This patient does not meet the inclusion and/or exclusion criteria for this study. No further data entry is required for this patient.')
      return dispatch(toggleShowChartGeneralValidation(true))
    }

    dispatch(savingChartPhase())

    const newChart = {
      trial_site: trialSiteNumber || null,
      chart_reviewer: chartReviewerId || null,
      chart_reviewer_role: chartReviewerRole || null,
      chart_reviewer_role_other_text: chartReviewerRoleOther,
      patient_identifier: patientIdentifier || null,
      screening_failure: false,
      site,
      met_inclusion_criteria: metInclusionCriteria,
      met_exclusion_criteria: metExclusionCriteria,
      consent_date: formatDateForServer(informedConsentDate),
      local_requirements_exemption: localRequirementsExemption,
    }
    
    let method, url

    if (patientChartUrl) {
      method = "PATCH"
      url = patientChartUrl
    } else {
      method = "POST"
      url = `${getServerUrl()}/patient-charts/`
    }

    axios({
      method,
      url,
      headers: { "Authorization": "JWT " + localStorage.getItem("jwt") },
      data: newChart,
    })
    .then(response => {
      if (response?.data?.screening_failure) {
        // return dispatch(toggleScreeningFailure(props, true))
        dispatch(handleChartFieldChange('screeningFailure', response.data.screening_failure))
        return dispatch(chartFieldSaved('savingChartPhase', false))
      }

      dispatch(handleChartFieldChange('screeningFailure', false))
      // if user's role was input manually, update teh user's last used role in the reducer
      if (chartReviewerRole !== currentUser.last_role_used ||
        chartReviewerRoleOther !== currentUser.last_role_used_other) {
        const user = {
          ...currentUser,
          last_role_used: chartReviewerRole,
          last_role_used_other: chartReviewerRoleOther,
        }
        dispatch(setUser(user))
      }

      alertChartHasBeenSaved(props)
      dispatch(chartGeneralInfoHasBeenSaved(response.data, goToNextPhase))
    })
    .catch(error => {
      if (error?.response?.data?.screening_failure) {
        return dispatch(dispatch(handleChartFieldChange('screeningFailure', true)))
      }
      dispatch(errorSavingChartPhase(error, "Patient Chart General Information", dispatch))
    })
  }
}


// export const toggleScreeningFailure = (props, screeningFailure) => {
//   return (dispatch) => {
//     dispatch(savingChartPhase())
//     axios({
//       method: 'PATCH',
//       url: `${props.patientChartUrl}`,
//       headers: { "Authorization": "JWT " + localStorage.getItem("jwt") },
//       data: { screening_failure: screeningFailure },
//     })
//     .then(response => {
//       dispatch(handleChartFieldChange('screeningFailure', response.data.screening_failure))
//       if (response.data.screening_failure === true) {
//         return dispatch(isChartCompleted(props, response.data.screening_failure))
//       } else {
//         return dispatch(chartFieldSaved('savingChartPhase', false))
//       }
//     })
//     .catch(error => {
//       dispatch(errorSavingChartPhase(error, "Patient Chart", dispatch))
//     })
//   }
// }

export const toggleChartCompleted = (props, chartCompleted) => {
  return (dispatch) => {
    dispatch(savingChartPhase())
    axios({
      method: 'PATCH',
      url: `${props.patientChartUrl}toggle_complete/`,
      headers: { "Authorization": "JWT " + localStorage.getItem("jwt") },
      data: { completed: chartCompleted },
    })
    .then(response => {
      dispatch(chartHasBeenCompleted())
      props.routerProps.history.push('/charts')
    })
    .catch(error => {
      dispatch(errorSavingChartPhase(error, "Patient Chart", dispatch))
    })
  }
}

export const isChartCompleted = (props, screeningFailure) => {
  return (dispatch, getState) => {
    const chartIsSaving = getState().chartFormReducer.savingChartPhase
    const checkingIfChartIsComplete = getState().chartFormReducer.checkingIfChartIsComplete
    if (chartIsSaving || checkingIfChartIsComplete) {
      return dispatch({ type: '' })
    }

    let splitUrl = props.patientChartUrl.split('/')
    let chartId = splitUrl[splitUrl.length -2]
    dispatch(savingChartPhase())
    dispatch(toggleCheckingIfChartIsComplete(true))
    axios({
      method: 'GET',
      url: `${getServerUrl()}/incomplete/${chartId}/`,
      headers: { "Authorization": "JWT " + localStorage.getItem("jwt") },
      params: {chart_id: chartId}
    })
    .then(response => {
      const incomplete = response.data.filter(item => !item.complete)
      if (incomplete.length > 0) {
        //if any item is not complete, store response.data in state and use to trigger 'incomplete modal'
        dispatch(chartIncompleteOnSubmit(incomplete))
        dispatch(chartFieldSaved('savingChartPhase', false))
      } else if (incomplete.length === 0 && !screeningFailure) {
        dispatch(toggleChartCompleted(props, true))
      } else if (incomplete.length === 0) {
        dispatch(chartFieldSaved('savingChartPhase', false))
      }
      dispatch(toggleCheckingIfChartIsComplete(false))
    })
    .catch(error => {
      dispatch(errorSavingChartPhase(error, "Patient Chart", dispatch))
      dispatch(toggleCheckingIfChartIsComplete(false))
    })
  }
}

const handleToggleChartRequiresRevisions = (props, requiresRevisions) => {
  return dispatch => {
    axios({
      method: 'PATCH',
      url: `${props.patientChartUrl}toggle_requires_revisions/`,
      headers: { "Authorization": "JWT " + localStorage.getItem("jwt") },
      data: { requires_revisions: requiresRevisions },
    })
      .then(response => {
        dispatch(chartHasBeenCompleted())
        props.routerProps.history.push('/charts')
      })
      .catch(error => {
        dispatch(errorSavingChartPhase(error, "Patient Chart", dispatch))
      })
  }
}


export const toggleChartRequiresRevisions = (props, requiresRevisions) => {
  return (dispatch, getState) => {
    const chartIsSaving = getState().chartFormReducer.savingChartPhase
    const checkingIfChartIsComplete = getState().chartFormReducer.checkingIfChartIsComplete
    if (chartIsSaving || checkingIfChartIsComplete) {
      return dispatch({ type: '' })
    }

    dispatch(savingChartPhase())
    if (requiresRevisions === false) {
      dispatch(toggleCheckingIfChartIsComplete(true))
      let splitUrl = props.patientChartUrl.split('/')
      let chartId = splitUrl[splitUrl.length - 2]

      axios({
        method: 'GET',
        url: `${getServerUrl()}/incomplete/${chartId}/`,
        headers: { "Authorization": "JWT " + localStorage.getItem("jwt") },
        params: { chart_id: chartId }
      })
        .then(response => {
          const incomplete = response.data.filter(item => !item.complete)
          if (incomplete.length > 0) {
            //if any item is not complete, store response.data in state and use to trigger 'incomplete modal'
            dispatch(chartIncompleteOnSubmit(incomplete))
            dispatch(chartFieldSaved('savingChartPhase', false))
          } else if (incomplete.length === 0) {
            dispatch(handleToggleChartRequiresRevisions(props, requiresRevisions))
          }
          dispatch(toggleCheckingIfChartIsComplete(false))
        })
        .catch(error => {
          dispatch(errorSavingChartPhase(error, "Patient Chart", dispatch))
          dispatch(toggleCheckingIfChartIsComplete(false))
        })
    } else {
      dispatch(handleToggleChartRequiresRevisions(props, requiresRevisions))
    }
  }
}

export const toggleChartApproved = (props, approved) => {
  return (dispatch) => {
    dispatch(savingChartPhase())
    axios({
      method: 'PATCH',
      url: props.patientChartUrl,
      headers: { "Authorization": "JWT " + localStorage.getItem("jwt") },
      data: { approved },
    })
    .then(response => {
      dispatch(chartHasBeenCompleted())
      props.routerProps.history.push('/charts')
    })
    .catch(error => {
      dispatch(errorSavingChartPhase(error, "Patient Chart", dispatch))
    })
  }
}


export const handleToggleChartApprovedBySite = (props, approved_by_site) => {
  return (dispatch) => {
    dispatch(savingChartPhase())
    let requires_revisions = false
    if (approved_by_site === false) {
      requires_revisions = true
    }
    axios({
      method: 'PATCH',
      url: `${props.patientChartUrl}toggle_approved_by_site/`,
      headers: { "Authorization": "JWT " + localStorage.getItem("jwt") },
      data: { approved_by_site, requires_revisions},
    })
    .then(response => {
      dispatch(chartHasBeenCompleted())
      props.routerProps.history.push('/charts')
    })
    .catch(error => {
      dispatch(errorSavingChartPhase(error, "Patient Chart", dispatch))
    })
  }
}

export const toggleChartApprovedBySite = (props, approved_by_site) => {
  return (dispatch) => {
    dispatch(savingChartPhase())
    if (approved_by_site === true) {
      dispatch(toggleCheckingIfChartIsComplete(true))
      let splitUrl = props.patientChartUrl.split('/')
      let chartId = splitUrl[splitUrl.length - 2]

      axios({
        method: 'GET',
        url: `${getServerUrl()}/incomplete/${chartId}/`,
        headers: { "Authorization": "JWT " + localStorage.getItem("jwt") },
        params: { chart_id: chartId }
      })
        .then(response => {
          const incomplete = response.data.filter(item => !item.complete)
          if (incomplete.length > 0) {
            //if any item is not complete, store response.data in state and use to trigger 'incomplete modal'
            dispatch(chartIncompleteOnSubmit(incomplete, true))
            dispatch(chartFieldSaved('savingChartPhase', false))
          } else if (incomplete.length === 0) {
            dispatch(handleToggleChartApprovedBySite(props, approved_by_site))
          }
          dispatch(toggleCheckingIfChartIsComplete(false))
        })
        .catch(error => {
          dispatch(errorSavingChartPhase(error, "Patient Chart", dispatch))
          dispatch(toggleCheckingIfChartIsComplete(false))
        })
    } else {
      dispatch(handleToggleChartApprovedBySite(props, approved_by_site))
    }
  }
}

export const toggleChartApprovedByDataManager = (props, approved_by_data_manager) => {
  return (dispatch) => {
    dispatch(savingChartPhase())
    axios({
      method: 'PATCH',
      url: props.patientChartUrl,
      headers: { "Authorization": "JWT " + localStorage.getItem("jwt") },
      data: { approved_by_data_manager },
    })
    .then(response => {
      dispatch(chartHasBeenCompleted())
      props.routerProps.history.push('/charts')
    })
    .catch(error => {
      dispatch(errorSavingChartPhase(error, "Patient Chart", dispatch))
    })
  }
}


export const chartHasBeenCompleted = () => {
  return {type: "MARK_CHART_AS_COMPLETED"}
}


const fetchingChart = () => {
  return {type: 'FETCHING_CHART'}
}


const handleErrorFetchingChart = (message) => {
  return { type: 'HANDLE_ERROR_FETCHING_CHART', payload: message }
}

export const getChart = (routerProps, startAtPhaseOne) => {
  return (dispatch) => {
    dispatch(fetchingChart())
    const id = routerProps.match.params.id
    const pathName = routerProps.match.path
    axios({
      method: 'GET',
      url: `${getServerUrl()}/patient-charts/${id}/`,
      headers: { "Authorization": "JWT " + localStorage.getItem("jwt") },
      params: { front_end_path: pathName },
    })
    .then(response => {
      dispatch(setChartToForm(response.data, startAtPhaseOne))
    })
    .catch(error => {
      const errorMessage = handleErrorAndGenerateMessage(error, 'Patient Chart', dispatch)
      if (!error || !error.response || error.response.status !== 401) {
        dispatch(handleErrorFetchingChart(errorMessage))
      }
    })
  }
}

const deletingChart = () => {
  return { type: 'DELETING_CHART' }
}


const chartHasBeenDeleted = (url) => {
  return { type: "CHART_HAS_BEEN_DELETED", payload: url }
}


export const deleteChart = (url, props) => {
  return (dispatch) => {
    dispatch(deletingChart())
    axios({
      method: 'DELETE',
      url,
      headers: { "Authorization": "JWT " + localStorage.getItem("jwt") },
    })
    .then(response => {
      dispatch(chartHasBeenDeleted(url))

      if (props && props.routerProps) {
        props.routerProps.history.push('/charts')
      }

      if (props && props.alert) {
        props.alert.success("Chart has been deleted")
      }
    })
    .catch(error => {
      const message = handleErrorAndGenerateMessage(error, 'deleting the chart', dispatch)
      
      if (props && props.alert && (!error || !error.response || error.response.status !== 401)) {
        props.alert.show(message)
      } else {
        return dispatch({ type: "ERROR_SAVING_CHART_PHASE", payload: message })
      }
    })
    .finally(() => {
      dispatch({ type: 'FINISHED_DELETING_CHART' })
    })
  }
}